import React, { useEffect, useState } from 'react';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import PauseRoundedIcon from '@mui/icons-material/PauseRounded';
import CampaignRoundedIcon from '@mui/icons-material/CampaignRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import SentimentVeryDissatisfiedRoundedIcon from '@mui/icons-material/SentimentVeryDissatisfiedRounded';
import PanToolRoundedIcon from '@mui/icons-material/PanToolRounded';
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';
import LightbulbRoundedIcon from '@mui/icons-material/LightbulbRounded';
import { spriteSrc } from '../Sprites/Sprite';
import type { SpriteName } from '../Sprites/Sprite';
import './InvestmentMindsetTrap.scss';

/**
 * 「投資心態的陷阱」, an in-house replacement for the Piktochart of the same name,
 * in the look of ai.portfoplus.com. The curve is the Piktochart's own data
 * series, so its shape is unchanged; its five floating callouts become numbered
 * markers on the curve and a row of cards under it, which survive a phone's
 * width and the longer English wording.
 */

/** The Piktochart's line-chart series: a top, a slide into a trough, a recovery to a new top. */
const SERIES = [
  425, 435, 445, 450, 455, 460, 460, 455, 450, 445, 440, 435, 430, 425, 420, 415, 410, 400, 380, 360, 340, 320, 300,
  280, 270, 265, 260, 255, 250, 250, 250, 255, 260, 265, 270, 280, 300, 320, 340, 360, 380, 400, 410, 415, 420, 425,
  430, 435, 440, 445, 450, 455, 460, 460, 455, 450, 445, 435, 425
];

const VIEW_W = 1000;
const VIEW_H = 420;
const PAD_X = 40;
const PAD_TOP = 40;
// Room under the axis for the time label and the trough's name.
const PAD_BOTTOM = 90;
const MIN = 240;
const MAX = 470;

const point = (i: number): [number, number] => [
  PAD_X + (i / (SERIES.length - 1)) * (VIEW_W - 2 * PAD_X),
  PAD_TOP + ((MAX - SERIES[i]) / (MAX - MIN)) * (VIEW_H - PAD_TOP - PAD_BOTTOM)
];

/** A Catmull-Rom spline through every point, as cubic Béziers. */
const curvePath = () => {
  const pts = SERIES.map((_, i) => point(i));
  return pts.reduce((d, [x, y], i) => {
    if (i === 0) return `M${x},${y}`;
    const [x0, y0] = pts[Math.max(i - 2, 0)];
    const [x1, y1] = pts[i - 1];
    const [x3, y3] = pts[Math.min(i + 1, pts.length - 1)];
    const c1 = [x1 + (x - x0) / 6, y1 + (y - y0) / 6];
    const c2 = [x - (x3 - x1) / 6, y - (y3 - y1) / 6];
    return `${d} C${c1[0]},${c1[1]} ${c2[0]},${c2[1]} ${x},${y}`;
  }, '');
};

const LINE = curvePath();
const [FIRST_X] = point(0);
const [LAST_X] = point(SERIES.length - 1);
const AREA = `${LINE} L${LAST_X},${VIEW_H - PAD_BOTTOM} L${FIRST_X},${VIEW_H - PAD_BOTTOM} Z`;

/** `cost` — a move that loses money (buy high, sell low); `missed` — a chance let go. */
type Tone = 'cost' | 'missed';

interface Moment {
  key: string;
  /** Index into SERIES where the marker sits. */
  at: number;
  tone: Tone;
  Icon: typeof CampaignRoundedIcon;
  /** Where the moment's name sits on the chart: offset from the marker, and which end it hangs from. */
  label: { dx: number; dy: number; anchor: 'start' | 'middle' | 'end' };
}

// Each name sits on the side of its marker away from the curve: the tops above and outward,
// the slopes on their open side, the trough under the axis.
const MOMENTS: Moment[] = [
  { key: 'euphoria', at: 6, tone: 'cost', Icon: CampaignRoundedIcon, label: { dx: 36, dy: -26, anchor: 'start' } },
  { key: 'panic', at: 20, tone: 'cost', Icon: WarningAmberRoundedIcon, label: { dx: -44, dy: 10, anchor: 'end' } },
  {
    key: 'despair',
    at: 29,
    tone: 'missed',
    Icon: SentimentVeryDissatisfiedRoundedIcon,
    label: { dx: 0, dy: 62, anchor: 'middle' }
  },
  { key: 'rebound', at: 39, tone: 'missed', Icon: PanToolRoundedIcon, label: { dx: 44, dy: 10, anchor: 'start' } },
  {
    key: 'euphoria-again',
    at: 52,
    tone: 'cost',
    Icon: ReplayRoundedIcon,
    label: { dx: -36, dy: -26, anchor: 'end' }
  }
];

/** The face the investor wears at each of MOMENTS, in order. */
const FACES: SpriteName[] = ['face-euphoria', 'face-panic', 'face-despair', 'face-hesitate', 'face-greed'];

/** How long the walk-through rests on each moment. */
const STEP_MS = 2200;

interface Intl {
  locale: string;
  formatMessage: (descriptor: { id: string }) => string;
}

/** The app's `zh` is Simplified Chinese; `lang` picks the CJK font in the stylesheet. */
const HTML_LANG: Record<string, string> = { zh: 'zh-CN', 'zh-Hant-HK': 'zh-HK' };

const InvestmentMindsetTrap = ({ intl }: { intl: Intl }) => {
  const t = (id: string) => intl.formatMessage({ id });
  // The moment being looked at, and whether the walk-through is stepping on by itself.
  const [active, setActive] = useState<number | null>(null);
  const [playing, setPlaying] = useState(false);
  const last = MOMENTS.length - 1;

  useEffect(() => {
    if (!playing) return;
    if (active === last) {
      setPlaying(false);
      return;
    }
    const timer = window.setTimeout(() => setActive(active === null ? 0 : active + 1), active === null ? 0 : STEP_MS);
    return () => window.clearTimeout(timer);
  }, [playing, active, last]);

  /** Pointing at a moment takes over from the walk-through. */
  const select = (i: number) => {
    setPlaying(false);
    setActive(i);
  };

  const togglePlay = () => {
    if (playing) return setPlaying(false);
    if (active === last) setActive(null);
    setPlaying(true);
  };

  const spot = active === null ? null : MOMENTS[active];
  const playLabel = playing ? 'imt-pause' : active === last ? 'imt-replay' : 'imt-play';

  return (
    <div className="investment-mindset-trap" lang={HTML_LANG[intl.locale] || intl.locale}>
      <header className="imt-head">
        <h2 className="imt-title">
          <span>{t('imt-title')}</span>
        </h2>
        <p className="imt-lead">
          {/* No space here: Chinese runs on, and English carries its own space in `imt-lead`. */}
          {t('imt-lead')}
          <strong className="imt-irrational">{t('imt-lead-irrational')}</strong>
          {t('imt-lead-end')}
        </p>
        <p className="imt-lead-2">{t('imt-lead-2')}</p>
      </header>

      <figure className="imt-chart">
        <svg viewBox={`0 0 ${VIEW_W} ${VIEW_H}`} role="img" aria-label={t('imt-chart-label')}>
          <defs>
            <linearGradient id="imt-area" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#3966F8" stopOpacity="0.22" />
              <stop offset="100%" stopColor="#3966F8" stopOpacity="0" />
            </linearGradient>
          </defs>
          {[0.25, 0.5, 0.75].map(f => {
            const y = PAD_TOP + f * (VIEW_H - PAD_TOP - PAD_BOTTOM);
            return <line key={f} className="imt-grid" x1={PAD_X} x2={VIEW_W - PAD_X} y1={y} y2={y} />;
          })}
          <line className="imt-axis" x1={PAD_X} x2={VIEW_W - PAD_X} y1={VIEW_H - PAD_BOTTOM} y2={VIEW_H - PAD_BOTTOM} />
          <text className="imt-axis-label" x={PAD_X} y={PAD_TOP - 14}>
            {t('imt-axis-price')}
          </text>
          <text className="imt-axis-label" x={VIEW_W - PAD_X} y={VIEW_H - PAD_BOTTOM + 34} textAnchor="end">
            {t('imt-axis-time')} →
          </text>
          <path className="imt-area" d={AREA} fill="url(#imt-area)" />
          <path className="imt-line" d={LINE} pathLength={1} />
          {/* The empty sky over the trough: the investor's face, which follows the moment picked. */}
          <image
            href={spriteSrc(active === null ? 'face-calm' : FACES[active])}
            data-sprite={active === null ? 'face-calm' : FACES[active]}
            className="imt-buddy"
            x={430}
            y={34}
            width={140}
            height={140}
          />
          {MOMENTS.map(({ key, at, tone, label }, i) => {
            const [x, y] = point(at);
            return (
              <g
                key={key}
                className={`imt-marker is-${tone}${i === active ? ' is-active' : ''}`}
                style={{ animationDelay: `${1.2 + i * 0.18}s`, transformOrigin: `${x}px ${y}px` }}
                data-moment={key}
                tabIndex={0}
                role="button"
                aria-label={`${i + 1}. ${t(`imt-${key}-title`)}`}
                aria-pressed={i === active}
                onMouseEnter={() => select(i)}
                onFocus={() => select(i)}
                onClick={() => select(i)}
              >
                <circle className="imt-marker-halo" cx={x} cy={y} r={40} />
                <circle className="imt-marker-dot" cx={x} cy={y} r={27} />
                <text x={x} y={y} dy="0.36em" textAnchor="middle">
                  {i + 1}
                </text>
                <text
                  className="imt-marker-label"
                  x={x + label.dx}
                  y={y + label.dy}
                  dy="0.36em"
                  textAnchor={label.anchor}
                >
                  {t(`imt-${key}-title`)}
                </text>
              </g>
            );
          })}
        </svg>
        <figcaption className="imt-legend">
          <span className="is-cost">{t('imt-legend-cost')}</span>
          <span className="is-missed">{t('imt-legend-missed')}</span>
        </figcaption>
      </figure>

      <div className="imt-explore">
        <button type="button" className="imt-play" onClick={togglePlay}>
          {playing ? <PauseRoundedIcon aria-hidden="true" /> : <PlayArrowRoundedIcon aria-hidden="true" />}
          {t(playLabel)}
        </button>
        <div className={`imt-spotlight${spot ? ` is-${spot.tone}` : ''}`} aria-live="polite">
          {spot && active !== null ? (
            <>
              <span className="imt-spotlight-icon" aria-hidden="true">
                <spot.Icon />
              </span>
              <div>
                <h3>
                  <span>{active + 1}</span>
                  {t(`imt-${spot.key}-title`)}
                </h3>
                <p>{t(`imt-${spot.key}`)}</p>
              </div>
            </>
          ) : (
            <p className="imt-hint">{t('imt-hint')}</p>
          )}
        </div>
      </div>

      <ol className={`imt-moments${active === null ? '' : ' has-active'}`}>
        {MOMENTS.map(({ key, tone, Icon }, i) => (
          <li
            key={key}
            className={`imt-moment is-${tone}${i === active ? ' is-active' : ''}`}
            tabIndex={0}
            aria-current={i === active}
            onMouseEnter={() => select(i)}
            onFocus={() => select(i)}
            onClick={() => select(i)}
          >
            <div className="imt-moment-top">
              <span className="imt-moment-icon" aria-hidden="true">
                <Icon />
              </span>
              <span className="imt-moment-no">{i + 1}</span>
            </div>
            <h3>{t(`imt-${key}-title`)}</h3>
            <p>{t(`imt-${key}`)}</p>
          </li>
        ))}
      </ol>

      <p className="imt-conclusion">
        <span>{t('imt-conclusion')}</span>
      </p>

      <div className="imt-tip">
        <LightbulbRoundedIcon aria-hidden="true" />
        <p>{t('imt-tip')}</p>
      </div>

      <p className="imt-disclaimer">
        {t('imt-disclaimer-1')}
        <br />
        {t('imt-disclaimer-2')}
      </p>
    </div>
  );
};

export default InvestmentMindsetTrap;
